import { memo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Avatar, IconButton, Tooltip, Typography } from '@mui/material'
import { SelectableGroup, createSelectable } from 'react-selectable-fast'
import CloseIcon from '@mui/icons-material/Close'
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import BeachAccessIcon from '@mui/icons-material/BeachAccess'
import { useCellClick } from '../use-cell-click'
import { cellId, dateKey, formatDayHeader, getDaySchedule, getException } from '../utils'

const NAME_COL = 220
const ROW_H = 56
const HEADER_H = 44

/**
 * Computes column width in pixels based on total visible days count.
 *
 * @param {number} count - Total visible days
 * @returns {number} Width in pixels
 */
function colWidthFor(count) {
  if (count <= 7) return 150
  if (count <= 14) return 120
  return 96
}

// A real booking always wins over an exception — it just gets a badge instead.
const BOOKED_STATUSES = new Set(['partial', 'full', 'over'])

/**
 * Vector icon component for exception dates (RDO or general holidays).
 *
 * @param {Object} props - Component props
 * @param {string} props.type - Exception type (e.g. 'RDO')
 * @param {string} [props.className] - CSS class
 * @param {Object} [props.style] - Style object
 * @returns {React.ReactElement} Vector icon element
 */
function ExceptionIcon({ type, className, style }) {
  const Icon = type === 'RDO' ? BeachAccessIcon : EventBusyIcon
  return <Icon className={className} style={style} />
}
ExceptionIcon.propTypes = { type: PropTypes.string, className: PropTypes.string, style: PropTypes.object }

const StatusCellInner = memo(function StatusCellInner({ sched, exception }) {
  if (exception && !BOOKED_STATUSES.has(sched.status)) {
    return (
      <Tooltip title={exception.name} arrow>
        <div className="status-chip status-exception">
          <ExceptionIcon type={exception.type} style={{ fontSize: 13, flexShrink: 0 }} />
          <span className="status-chip__name">{exception.name}</span>
        </div>
      </Tooltip>
    )
  }
  if (sched.status === 'off') {
    return (
      <div className="status-chip status-off">
        <span className="status-chip__name">Off</span>
      </div>
    )
  }
  if (sched.status === 'free') {
    return (
      <div className="status-chip status-free">
        <span className="status-chip__name">Available</span>
        <span className="status-chip__sub">{sched.capacityHours}h open</span>
      </div>
    )
  }
  return (
    <div className={`status-chip status-${sched.status}`}>
      {exception && (
        <Tooltip title={exception.name} arrow>
          <ExceptionIcon type={exception.type} className="status-chip__exception-badge" style={{ fontSize: 12 }} />
        </Tooltip>
      )}
      <Tooltip title={sched.project || ''} arrow>
        <span className="status-chip__name">{sched.project || '—'}</span>
      </Tooltip>
      <span className="status-chip__sub">
        {sched.bookedHours}h / {sched.capacityHours}h
        {sched.status === 'partial' ? ' booked' : ''}
      </span>
    </div>
  )
})
StatusCellInner.propTypes = {
  sched: PropTypes.object.isRequired,
  exception: PropTypes.shape({ name: PropTypes.string, type: PropTypes.string }),
}

/**
 * Selectable table cell component for the staff scheduling grid.
 *
 * @param {object} props - Component props
 * @param {React.Ref} props.selectableRef - Ref provided by createSelectable
 * @param {boolean} props.isSelecting - Whether the cell is currently within an active drag selection box
 * @param {object} props.sched - Daily schedule data (bookedHours, capacityHours, status, project)
 * @param {boolean} [props.isOff] - Whether the staff member has the day off
 * @param {object} [props.exception] - Date exception data (holiday, RDO, leave)
 * @param {boolean} [props.forceSelected] - Whether the cell is persistently selected in state
 * @param {Function} [props.onToggle] - Toggle selection callback
 * @param {Function} [props.onClickCell] - Handler invoked when clicking an assigned shift cell
 * @returns {JSX.Element} Rendered table cell
 */
const SelectableCell = createSelectable(function SelectableCellInner({
  selectableRef, isSelecting, sched, isOff, exception, forceSelected, onToggle, onClickCell,
}) {
  const highlighted = isSelecting || forceSelected
  const isBooked = sched.bookedHours > 0

  const handleCellAction = useCallback(() => {
    if (forceSelected) {
      if (onToggle) onToggle()
      return
    }
    if (isBooked && onClickCell) {
      onClickCell()
      return
    }
    if (onToggle) {
      onToggle()
    }
  }, [forceSelected, isBooked, onClickCell, onToggle])

  const cellClickHandlers = useCellClick(handleCellAction)

  return (
    <td
      ref={selectableRef}
      className={[
        'schedule-td',
        highlighted ? 'selected-td' : '',
        isOff ? 'is-off' : '',
        exception ? 'is-exception' : '',
        isBooked ? 'is-booked-interactive' : '',
      ].filter(Boolean).join(' ')}
      {...cellClickHandlers}
    >
      <StatusCellInner sched={sched} exception={exception} />
    </td>
  )
})
SelectableCell.propTypes = {
  selectableKey: PropTypes.string.isRequired,
  sched: PropTypes.object.isRequired,
  isOff: PropTypes.bool,
  exception: PropTypes.shape({ name: PropTypes.string, type: PropTypes.string }),
  forceSelected: PropTypes.bool,
  onToggle: PropTypes.func,
  onClickCell: PropTypes.func,
}

/**
 * "Show more" affordance for paging the next batch of the alphabetical roster into view.
 *
 * @param {Object} props - Component props
 * @param {number} props.remainingCount - Number of unrendered roster staff remaining
 * @param {Function} props.onShowMore - Callback to load the next roster batch
 * @returns {React.ReactElement} Show more button element
 */
function ShowMoreButton({ remainingCount, onShowMore }) {
  return (
    <button className="show-more-btn d-flex align-items-center gap-1" onClick={onShowMore} type="button">
      <ExpandMoreIcon sx={{ fontSize: 18 }} />
      Show {Math.min(10, remainingCount)} more
      {remainingCount > 10 ? ` (${remainingCount} remaining)` : ''}
    </button>
  )
}
ShowMoreButton.propTypes = {
  remainingCount: PropTypes.number.isRequired,
  onShowMore: PropTypes.func.isRequired,
}

/**
 * Staff Scheduling Grid component rendering interactive roster rows and day status cells.
 *
 * @param {Object} props - Component props
 * @param {Array<Object>} props.staff - Visible staff members list
 * @param {Array<Date>} props.days - Visible calendar days
 * @param {Map} props.scheduleMap - Map of booked schedules keyed by "userId:yyyy-MM-dd"
 * @param {Map} [props.availability] - Staff availability map
 * @param {Map} [props.exceptionDates] - Map of date exceptions
 * @param {number} props.standardHours - Daily standard work hours
 * @param {Object} props.selection - Grid selection state and handlers
 * @param {Function} props.onRemoveStaff - Handler to remove staff from view
 * @param {Function} props.onAddStaff - Handler to open add staff drawer
 * @param {boolean} [props.hasMore] - Whether more staff are available to page
 * @param {number} [props.remainingCount] - Remaining count of unrendered staff
 * @param {Function} [props.onShowMore] - Handler to page more staff
 * @param {Function} [props.onEditDay] - Handler invoked when clicking an assigned day cell
 * @returns {React.ReactElement} Rendered staff schedule grid
 */
export default function StaffGrid({
  staff, days, scheduleMap, availability, exceptionDates, standardHours, selection,
  onRemoveStaff, onAddStaff, hasMore, remainingCount, onShowMore, onEditDay,
}) {
  const colWidth = colWidthFor(days.length)
  const { selected, groupRef, clearingKey, onSelectionFinish, toggleCell, selectRow, selectColumn } = selection

  if (staff.length === 0) {
    return (
      <div className="schedule-grid d-flex flex-column align-items-center justify-content-center p-5 gap-3">
        <Typography variant="h6" className="text-secondary">No resources in view</Typography>
        <Typography variant="body2" className="text-secondary">Add staff to start building allocations.</Typography>
        <div className="d-flex align-items-center gap-2">
          <button className="primarybtn btn-ch d-flex align-items-center gap-1" onClick={onAddStaff} type="button">
            <PersonAddAlt1Icon sx={{ fontSize: 16 }} /> Add Staff
          </button>
          {hasMore && <ShowMoreButton remainingCount={remainingCount} onShowMore={onShowMore} />}
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      <div className="schedule-grid" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* LEFT — frozen name column (outside SelectableGroup to keep click events clean) */}
        <div style={{ flexShrink: 0, width: NAME_COL, display: 'flex', flexDirection: 'column', zIndex: 3 }}>
          <div
            className="col-frozen"
            style={{ height: HEADER_H, display: 'flex', alignItems: 'center', padding: '0 12px', flexShrink: 0 }}
          >
            <span style={{ fontSize: 11, fontWeight: 700, color: '#555', textTransform: 'uppercase' }}>
              Resource
            </span>
          </div>
          {staff.map((s, row) => (
            <div
              key={s.id}
              className="row-frozen"
              style={{ height: ROW_H, display: 'flex', alignItems: 'center', padding: '0 8px', flexShrink: 0, cursor: 'pointer' }}
              onClick={() => selectRow(row)}
              title="Click to select whole row"
            >
              <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'var(--brand-color-1)', flexShrink: 0 }}>
                {s.initials}
              </Avatar>
              <div className="flex-grow-1 overflow-hidden mx-2">
                <div className="staff-name text-truncate">{s.name}</div>
                <div className="staff-meta text-truncate">{s.role}</div>
              </div>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onRemoveStaff(s.id) }}
                aria-label={`Remove ${s.name}`}
              >
                <CloseIcon sx={{ fontSize: 16, color: '#d0021b' }} />
              </IconButton>
            </div>
          ))}
        </div>

        {/* RIGHT — scrollable data columns */}
        <div className="schedule-data-scroll" style={{ flex: 1, overflow: 'auto' }}>
          <div style={{ width: days.length * colWidth }}>

            {/* Day header row — click to select whole column */}
            <div style={{ display: 'flex', height: HEADER_H, position: 'sticky', top: 0, zIndex: 2 }}>
              {days.map((d, col) => {
                const f = formatDayHeader(d)
                return (
                  <div
                    key={col}
                    className={`col-day-header${f.isToday ? ' is-today' : ''}${f.isWeekend ? ' is-weekend' : ''}`}
                    style={{ width: colWidth, flexShrink: 0 }}
                    onClick={() => selectColumn(col)}
                    title="Click to select whole column"
                  >
                    <div style={{ fontSize: 11, fontWeight: 700, color: f.isToday ? 'var(--brand-color-2)' : '#333' }}>
                      {f.weekday}
                    </div>
                    <div style={{ fontSize: 10, color: '#666' }}>{f.dayMonth}</div>
                  </div>
                )
              })}
            </div>

            {/* SelectableGroup wraps data cells only — single click handled via toggleCell */}
            <SelectableGroup
              key={clearingKey}
              ref={groupRef}
              onSelectionFinish={onSelectionFinish}
              scrollContainer=".schedule-data-scroll"
              enableDeselect
              tolerance={4}
              allowClickWithoutSelected
              resetOnStart={true}
              selectOnClick={false}
              component="div"
              style={{ position: 'relative' }}
            >
              <table style={{ tableLayout: 'fixed', borderCollapse: 'collapse', width: '100%' }}>
                <colgroup>
                  {days.map((_, col) => <col key={col} style={{ width: colWidth }} />)}
                </colgroup>
                <tbody>
                  {staff.map((s) => (
                    <tr key={s.id} style={{ display: 'table-row', height: ROW_H }}>
                      {days.map((d) => {
                        const dayKeyStr = dateKey(d)
                        const key = cellId(s.id, dayKeyStr)
                        const sched = getDaySchedule(scheduleMap, s.id, dayKeyStr, availability, standardHours)
                        const exception = getException(exceptionDates, s.id, dayKeyStr)
                        return (
                          <SelectableCell
                            key={key}
                            selectableKey={key}
                            sched={sched}
                            isOff={sched.status === 'off'}
                            exception={exception}
                            forceSelected={selected.has(key)}
                            onToggle={() => toggleCell(key)}
                            onClickCell={() => onEditDay && onEditDay(s, dayKeyStr, sched)}
                          />
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </SelectableGroup>

          </div>
        </div>
      </div>

      {/* Paged loading footer — keeps the initial payload small for large rosters */}
      {hasMore && (
        <div className="schedule-grid-footer d-flex align-items-center justify-content-center">
          <ShowMoreButton remainingCount={remainingCount} onShowMore={onShowMore} />
        </div>
      )}
    </div>
  )
}

StaffGrid.propTypes = {
  staff: PropTypes.array.isRequired,
  days: PropTypes.array.isRequired,
  scheduleMap: PropTypes.instanceOf(Map).isRequired,
  availability: PropTypes.instanceOf(Map),
  exceptionDates: PropTypes.instanceOf(Map),
  standardHours: PropTypes.number,
  selection: PropTypes.object.isRequired,
  onRemoveStaff: PropTypes.func.isRequired,
  onAddStaff: PropTypes.func.isRequired,
  hasMore: PropTypes.bool,
  remainingCount: PropTypes.number,
  onShowMore: PropTypes.func,
  onEditDay: PropTypes.func,
}
